/**
 * Phase M.1 (2026-08-21) — Unit-number double-assignment warning banner.
 *
 * Given a ``unit_number`` (and optional ``excludeRentalId`` for the
 * "editing myself" case), debounces a lookup against
 * ``GET /api/admin/rentals/equipment-status`` and surfaces a yellow
 * warning banner if the unit is currently held by another live rental.
 *
 * Also owns the ``override_reason`` textarea that must be filled (≥10
 * chars) before a force-save can succeed. Uncontrolled: parent
 * receives the current state via ``onStateChange({ inConflict,
 * conflict, overrideReason })`` and decides whether to append
 * ``?force=true`` to its API call and forward ``override_reason`` in
 * the request body.
 *
 * Cleared automatically when the parent changes ``unitNumber`` back to
 * a non-conflicting value.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";


export default function UnitNumberConflictBanner({
    unitNumber,
    excludeRentalId,
    onStateChange,
}) {
    const [conflict, setConflict] = useState(null);
    const [overrideReason, setOverrideReason] = useState("");
    const debounceRef = useRef(null);
    const seqRef = useRef(0);

    const trimmed = (unitNumber || "").trim();

    useEffect(() => {
        // Clear conflict + reason when the input drops the value entirely.
        if (!trimmed) {
            setConflict(null);
            setOverrideReason("");
            if (debounceRef.current) clearTimeout(debounceRef.current);
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            const mySeq = ++seqRef.current;
            try {
                const { data } = await api.get(
                    "/admin/rentals/equipment-status",
                    {
                        params: {
                            unit_number: trimmed,
                            ...(excludeRentalId
                                ? { exclude_rental_id: excludeRentalId }
                                : {}),
                        },
                    },
                );
                // Ignore stale responses.
                if (mySeq !== seqRef.current) return;
                if (data?.is_currently_assigned) {
                    setConflict(data);
                } else {
                    setConflict(null);
                    setOverrideReason("");
                }
            } catch {
                // Silently swallow — a network hiccup shouldn't block
                // the parent form from submitting. If the server IS
                // holding a conflict, the 409 will surface on submit.
                if (mySeq !== seqRef.current) return;
                setConflict(null);
            }
        }, 400);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [trimmed, excludeRentalId]);

    // Bubble state up to the parent so it can gate its submit + append
    // ``?force=true`` when required. Kept in an effect so the parent
    // doesn't need to re-render on every keystroke of override_reason.
    const state = useMemo(() => ({
        inConflict:      !!conflict,
        conflict,
        overrideReason:  overrideReason.trim(),
        canForceSubmit:  !!conflict && overrideReason.trim().length >= 10,
    }), [conflict, overrideReason]);

    useEffect(() => {
        onStateChange?.(state);
    }, [state, onStateChange]);

    if (!conflict) return null;
    return (
        <div
            className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="unit-number-conflict-warning"
        >
            <div className="flex items-start gap-2">
                <AlertTriangle
                    size={16} className="mt-0.5 flex-none"
                />
                <div className="min-w-0">
                    <div>
                        Unit <span className="font-semibold">
                            {conflict.unit_number}
                        </span>{" "}
                        is currently assigned to{" "}
                        <span className="font-semibold">
                            {conflict.current_customer_name}
                        </span>{" "}
                        on rental{" "}
                        <span className="font-mono">
                            {(conflict.current_rental_id || "").slice(0, 8)}
                        </span>{" "}
                        (status{" "}
                        <span className="font-semibold">
                            {conflict.current_rental_status}
                        </span>
                        ). Saving will let both rentals hold this unit —
                        the other rental will not be updated.
                    </div>
                    {conflict.current_rental_id && (
                        <a
                            href={`/admin/rentals/${conflict.current_rental_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-block text-xs font-medium text-blue-800 hover:text-blue-900 underline"
                            data-testid="unit-number-conflict-view-link"
                        >
                            View current rental →
                        </a>
                    )}
                </div>
            </div>
            <div className="mt-3 space-y-1.5">
                <Label htmlFor="unit-number-force-override-reason">
                    Override reason <span className="text-red-600">*</span>
                </Label>
                <Textarea
                    id="unit-number-force-override-reason"
                    data-testid="unit-number-force-override-reason"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Why is this reassignment necessary? (min. 10 characters)"
                    rows={2}
                />
                {overrideReason.trim() && overrideReason.trim().length < 10 && (
                    <p className="text-xs text-red-600">
                        Override reason must be at least 10 characters.
                    </p>
                )}
            </div>
        </div>
    );
}
